'use client';

import { useState } from 'react';
import Link from 'next/link';
import { strings } from '@/content/strings';
import { formatTimeWithZone, formatTimeZoneLabel } from '@/lib/dates';

type BookingView = {
  id: string;
  status: string;
  displayStatus?: string;
  checkIn: string;
  checkOut: string;
  checkInTime: string;
  checkOutTime: string;
  nights: number;
  numberOfGuests: number;
  specialRequests?: string | null;
  cancellationReason?: string | null;
  payment?: {
    hasPending: boolean;
    amountDue?: number;
    currency?: string;
    dueDate?: string;
    type?: string;
    isPayable?: boolean;
    nextDueDate?: string;
    nextAmountDue?: number;
    nextType?: string;
  };
  payments?: Array<{
    amount: number;
    currency?: string | null;
    dueDate: string;
    type: string;
    status: string;
    refundedAmount?: number | null;
    refundedAt?: string | null;
    refunds?: Array<{
      id: string;
      amount: number;
      currency?: string | null;
      refundedAt?: string | null;
      reason?: string | null;
    }>;
  }>;
  guest: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string | null;
  };
  property: {
    id: string;
    name: string;
    slug?: string | null;
    address?: Record<string, any> | null;
    timeZone?: string | null;
  };
  ratePlan: {
    name: string;
    description?: string | null;
  };
  priceBreakdown: Record<string, any>;
  lockAccesses?: Array<{
    id: string;
    deviceName: string;
    codes?: string[];
    status: string;
    startsAt: string;
    endsAt: string;
  }>;
};

type VerificationDetails = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

function formatMoney(amount: number | undefined, currency: string) {
  const value = typeof amount === 'number' ? amount : 0;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

export default function BookingDetailsCard({
  booking,
  verification,
}: {
  booking: BookingView;
  verification?: VerificationDetails | null;
}) {
  const bookingStrings = strings.screens.bookingDetail;
  const commonStrings = strings.common;
  const price = booking.priceBreakdown || {};
  const currency = price.currency || 'USD';
  const stayNights = price.nights ?? booking.nights;
  const guestSubtotal = (price.subtotal ?? 0) + (price.serviceFee ?? 0);
  const averageNightlyRate =
    stayNights > 0 ? Math.round(guestSubtotal / stayNights) : price.averageNightlyRate ?? price.nightlyRate;
  const nightlyLabel = price.averageNightlyRate ? bookingStrings.avgNightlyRate : bookingStrings.nightlyRate;
  const address = booking.property.address || {};
  const timeZone = booking.property.timeZone ?? 'UTC';
  const timeZoneLabel = formatTimeZoneLabel(timeZone, new Date(booking.checkIn));
  const checkInTimeLabel = formatTimeWithZone(booking.checkInTime, timeZoneLabel);
  const checkOutTimeLabel = formatTimeWithZone(booking.checkOutTime, timeZoneLabel);
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
    timeZoneName: 'short',
  });
  const payment = booking.payment;
  const paymentHistoryEntries = (booking.payments ?? [])
    .flatMap((entry) => {
      const status = entry.status.toLowerCase();
      const items: Array<{
        kind: 'payment' | 'refund';
        id: string;
        type: string;
        amount: number;
        currency?: string | null;
        date?: string | null;
        status: string;
      }> = [];
      if (status !== 'pending') {
        items.push({
          kind: 'payment',
          id: `${entry.type}-${entry.dueDate}`,
          type: entry.type,
          amount: entry.amount,
          currency: entry.currency ?? currency,
          date: entry.dueDate,
          status,
        });
      }
      const refunds =
        entry.refunds && entry.refunds.length > 0
          ? entry.refunds
          : (entry.refundedAmount ?? 0) > 0
            ? [
                {
                  id: `${entry.type}-${entry.dueDate}-refund`,
                  amount: entry.refundedAmount ?? 0,
                  currency: entry.currency ?? currency,
                  refundedAt: entry.refundedAt ?? entry.dueDate,
                },
              ]
            : [];
      refunds.forEach((refund) => {
        items.push({
          kind: 'refund',
          id: refund.id,
          type: entry.type,
          amount: refund.amount,
          currency: refund.currency ?? entry.currency ?? currency,
          date: refund.refundedAt ?? entry.refundedAt ?? entry.dueDate,
          status: 'refunded',
        });
      });
      return items;
    })
    .sort((a, b) => {
      const aTime = a.date ? new Date(a.date).getTime() : 0;
      const bTime = b.date ? new Date(b.date).getTime() : 0;
      return aTime - bTime;
    });
  const paymentTotals = (booking.payments ?? []).reduce(
    (acc, payment) => {
      const status = payment.status?.toUpperCase?.() ?? '';
      if (payment.refunds && payment.refunds.length > 0) {
        acc.refunded += payment.refunds.reduce((sum, refund) => sum + refund.amount, 0);
      } else if (payment.refundedAmount) {
        acc.refunded += payment.refundedAmount;
      } else if (status === 'REFUNDED') {
        acc.refunded += payment.amount;
      }
      return acc;
    },
    { refunded: 0 }
  );
  const totalRefunded = paymentTotals.refunded;
  const totalCharged = (price.total ?? 0) - totalRefunded;
  const displayStatus = booking.displayStatus ?? booking.status;
  const lockAccesses = booking.lockAccesses ?? [];
  const statusLabel =
    (bookingStrings.statusLabels as Record<string, string> | undefined)?.[displayStatus] ??
    displayStatus.toLowerCase();
  const statusClass = (() => {
    switch (displayStatus) {
      case 'PENDING':
        return 'bg-accent/20 text-accent-foreground';
      case 'PARTIALLY_REFUNDED':
        return 'bg-accent/15 text-accent-foreground';
      case 'REFUNDED':
        return 'bg-accent/10 text-accent-foreground';
      case 'APPROVED':
      case 'CONFIRMED':
        return 'bg-primary/15 text-foreground';
      case 'CANCELLED':
      case 'EXPIRED':
      case 'COMPLETED':
        return 'bg-ghost text-ghost-foreground';
      default:
        return 'bg-ghost text-ghost-foreground';
    }
  })();
  const canCancel = ['PENDING', 'APPROVED', 'CONFIRMED'].includes(booking.status);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [refundPreview, setRefundPreview] = useState<{
    amount: number;
    currency: string;
    percent: number;
    total: number;
    policyAmount: number;
    policyPercent: number;
    paidTotal: number;
  } | null>(null);

  const openCancelModal = async () => {
    setShowCancelModal(true);
    setPreviewError('');
    setRefundPreview(null);
    setPreviewLoading(true);
    try {
      const payload: Record<string, any> = {};
      if (verification) {
        payload.firstName = verification.firstName;
        payload.lastName = verification.lastName;
        payload.email = verification.email;
        payload.phone = verification.phone;
      }
      const res = await fetch(`/api/bookings/${booking.id}/refund-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        setPreviewError(data.error || bookingStrings.refundPreviewError);
        return;
      }
      setRefundPreview(data.data.refund);
    } catch (err) {
      setPreviewError(bookingStrings.refundPreviewError);
    } finally {
      setPreviewLoading(false);
    }
  };

  const requestCancel = async () => {
    if (!canCancel) return;
    setCancelling(true);
    setCancelError('');
    setCancelSuccess('');
    try {
      const payload: Record<string, any> = {
        reason: 'Cancelled by guest request',
      };
      if (verification) {
        payload.firstName = verification.firstName;
        payload.lastName = verification.lastName;
        payload.email = verification.email;
        payload.phone = verification.phone;
      }
      const res = await fetch(`/api/bookings/${booking.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        setCancelError(data.error || bookingStrings.cancelError);
        return;
      }
      setCancelSuccess(bookingStrings.cancelSuccess);
      setShowCancelModal(false);
    } catch (err) {
      setCancelError(bookingStrings.cancelError);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm p-6 text-card-foreground space-y-4 card-reveal">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{bookingStrings.status}</div>
          <h2 className="text-xl font-semibold text-foreground">{booking.property.name}</h2>
          <p className="text-sm text-muted-foreground">
            {address.city}, {address.state}
          </p>
          <div className="text-xs text-muted-foreground mt-1">{bookingStrings.bookingId(booking.id)}</div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${statusClass}`}>
            {statusLabel}
          </span>
          {booking.property.slug && (
            <Link href={`/properties/${booking.property.slug}`} className="text-xs font-semibold text-primary hover:opacity-80">
              {bookingStrings.viewProperty}
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
        <div className="space-y-4">
          <section className="rounded-xl border border-border bg-background p-4 space-y-3">
            <div className="text-sm font-semibold text-foreground">{bookingStrings.stayDetails}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">{commonStrings.labels.checkIn}</div>
                <div className="font-semibold text-foreground">{dateFormatter.format(new Date(booking.checkIn))}</div>
                <div className="text-xs text-muted-foreground">{checkInTimeLabel}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">{commonStrings.labels.checkOut}</div>
                <div className="font-semibold text-foreground">{dateFormatter.format(new Date(booking.checkOut))}</div>
                <div className="text-xs text-muted-foreground">{checkOutTimeLabel}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">{commonStrings.labels.nights}</div>
                <div className="font-semibold text-foreground">{booking.nights}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">{commonStrings.labels.guests}</div>
                <div className="font-semibold text-foreground">{booking.numberOfGuests}</div>
              </div>
            </div>
            {booking.specialRequests && (
              <div className="text-sm">
                <div className="text-xs text-muted-foreground">{bookingStrings.specialRequests}</div>
                <div className="font-medium text-foreground">{booking.specialRequests}</div>
              </div>
            )}
          </section>

          <section className="rounded-xl border border-border bg-background p-4 space-y-3">
            <div className="text-sm font-semibold text-foreground">{bookingStrings.lockAccessTitle}</div>
            {lockAccesses.length === 0 ? (
              <div className="text-xs text-muted-foreground">{bookingStrings.lockAccessEmpty}</div>
            ) : (
              <div className="space-y-2 text-xs text-muted-foreground">
                {lockAccesses.map((access) => (
                  <div
                    key={access.id}
                    className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-2 rounded-lg border border-border/60 bg-card px-3 py-2"
                  >
                    <div className="w-full sm:w-auto sm:min-w-[140px]">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {bookingStrings.lockAccessDevice}
                      </div>
                      <div className="text-foreground">{access.deviceName}</div>
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {bookingStrings.lockAccessCode}
                      </div>
                      <div className="text-foreground font-semibold">
                        {access.codes && access.codes.length > 0
                          ? access.codes.join(' / ')
                          : bookingStrings.lockAccessPending}
                      </div>
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[200px]">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {bookingStrings.lockAccessWindow}
                      </div>
                      <div className="text-foreground">
                        {dateTimeFormatter.format(new Date(access.startsAt))} –{' '}
                        {dateTimeFormatter.format(new Date(access.endsAt))}
                      </div>
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {bookingStrings.lockAccessStatus}
                      </div>
                      <div className="text-foreground">{access.status.toLowerCase()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-border bg-background p-4 space-y-3">
            <div className="text-sm font-semibold text-foreground">{bookingStrings.guestDetails}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">{commonStrings.labels.name}</div>
                <div className="font-semibold text-foreground">
                  {booking.guest.firstName} {booking.guest.lastName}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">{commonStrings.labels.email}</div>
                <div className="font-semibold text-foreground">{booking.guest.email || commonStrings.empty.notAvailable}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">{commonStrings.labels.phone}</div>
                <div className="font-semibold text-foreground">{booking.guest.phone || commonStrings.empty.notAvailable}</div>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-border bg-background p-4 space-y-2">
            <div className="text-sm font-semibold text-foreground">{bookingStrings.ratePlan}</div>
            <div className="text-sm text-foreground">
              <div className="font-semibold">{booking.ratePlan.name}</div>
              {booking.ratePlan.description && (
                <div className="text-xs text-muted-foreground">{booking.ratePlan.description}</div>
              )}
            </div>
          </section>
          <section className="rounded-xl border border-border bg-background p-4 space-y-3">
            <div className="text-sm font-semibold text-foreground">{bookingStrings.paymentHistory}</div>
            {paymentHistoryEntries.length === 0 ? (
              <div className="text-xs text-muted-foreground">{bookingStrings.paymentHistoryEmpty}</div>
            ) : (
              <div className="space-y-2 text-xs text-muted-foreground">
                {paymentHistoryEntries.map((entry, index) => {
                  const entryDate = entry.date ? dateTimeFormatter.format(new Date(entry.date)) : '—';
                  return (
                    <div
                      key={`${entry.id}-${index}`}
                      className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-2 border border-border/60 bg-card rounded-lg px-3 py-2"
                    >
                      <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {bookingStrings.paymentTypeLabel}
                        </div>
                        <div className="text-foreground">
                          {entry.kind === 'refund' ? bookingStrings.paymentRefundLabel : entry.type}
                        </div>
                      </div>
                      <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {bookingStrings.paymentAmountLabel}
                        </div>
                        <div className="text-foreground">
                          {formatMoney(entry.amount, entry.currency || currency)}
                        </div>
                      </div>
                      <div className="w-full sm:w-auto sm:min-w-[140px]">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {entry.kind === 'refund'
                            ? bookingStrings.paymentRefundedOnLabel
                            : bookingStrings.paymentDueLabel}
                        </div>
                        <div className="text-foreground">{entryDate}</div>
                      </div>
                      <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {bookingStrings.paymentStatusLabel}
                        </div>
                        <div className="text-foreground">{entry.status.toLowerCase()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {booking.cancellationReason && (
            <section className="rounded-xl border border-border bg-ghost p-4 space-y-2 text-ghost-foreground">
              <div className="text-sm font-semibold">{bookingStrings.cancellationReasonLabel}</div>
              <div className="text-sm">{booking.cancellationReason}</div>
            </section>
          )}
        </div>

        <aside className="space-y-4">
          <section className="rounded-xl border border-border bg-background p-4 space-y-3">
            <div className="text-sm font-semibold text-foreground">{bookingStrings.pricing}</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>{nightlyLabel}</span>
                <span>
                  {formatMoney(averageNightlyRate, currency)} × {stayNights}
                </span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{bookingStrings.subtotal}</span>
                <span>{formatMoney(guestSubtotal, currency)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{strings.screens.property.cleaningFee}</span>
                <span>{formatMoney(price.cleaningFee, currency)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{strings.screens.property.taxes}</span>
                <span>{formatMoney(price.taxes, currency)}</span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between font-semibold text-foreground">
                <span>{strings.screens.property.total}</span>
                <span>
                  {formatMoney(price.total, currency)} {currency}
                </span>
              </div>
              {(booking.payments ?? []).length > 0 && (
                <div className="border-t border-border pt-2 space-y-1 text-xs text-muted-foreground">
                  <div className="flex justify-between">
                    <span>{bookingStrings.totalCharged}</span>
                    <span>{formatMoney(totalCharged, currency)}</span>
                  </div>
                  {totalRefunded > 0 && (
                    <div className="flex justify-between">
                      <span>{bookingStrings.totalRefunded}</span>
                      <span>{formatMoney(-totalRefunded, currency)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
          {payment?.hasPending && payment.isPayable && (
            <section className="rounded-xl border border-border bg-background p-4 space-y-2">
              <div className="text-sm font-semibold text-foreground">{bookingStrings.paymentDue}</div>
              <div className="space-y-1 text-sm text-muted-foreground">
                <div>
                  {formatMoney(payment.amountDue, payment.currency || currency)}
                  {payment.dueDate && (
                    <>
                      {' '}
                      due {dateTimeFormatter.format(new Date(payment.dueDate))}
                    </>
                  )}
                </div>
                {payment.nextDueDate && payment.nextAmountDue !== undefined && (
                  <div className="text-xs text-muted-foreground">
                    Next payment: {formatMoney(payment.nextAmountDue, payment.currency || currency)} due{' '}
                    {dateTimeFormatter.format(new Date(payment.nextDueDate))}
                  </div>
                )}
              </div>
              <Link
                href={`/bookings/${booking.id}/pay`}
                className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90"
              >
                {bookingStrings.payNow}
              </Link>
            </section>
          )}
          {canCancel && (
            <section className="rounded-xl border border-border bg-background p-4 space-y-2">
              <div className="text-sm font-semibold text-foreground">{bookingStrings.manageBooking}</div>
              <p className="text-xs text-muted-foreground">
                {bookingStrings.manageBookingCopy}
              </p>
              <button
                type="button"
                onClick={openCancelModal}
                disabled={cancelling}
                className="rounded-lg border border-border bg-ghost px-4 py-2 text-xs font-semibold text-ghost-foreground hover:opacity-80 disabled:opacity-50"
              >
                {bookingStrings.cancelTitle}
              </button>
              {cancelError && <div className="text-xs text-destructive">{cancelError}</div>}
              {cancelSuccess && <div className="text-xs text-muted-foreground">{cancelSuccess}</div>}
            </section>
          )}
          <section className="rounded-xl border border-border bg-background p-4 space-y-2">
            <div className="text-sm font-semibold text-foreground">{bookingStrings.needHelp}</div>
            <div className="text-sm text-muted-foreground">
              {bookingStrings.needHelpCopy}
            </div>
          </section>
        </aside>
      </div>
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{bookingStrings.cancelModalTitle}</div>
                <h3 className="text-lg font-semibold text-foreground">{bookingStrings.cancelModalSubtitle}</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {bookingStrings.close}
              </button>
            </div>
            <div className="mt-4 space-y-3 text-sm">
              {previewLoading && <div className="text-muted-foreground">{bookingStrings.calculatingRefund}</div>}
              {previewError && <div className="text-destructive">{previewError}</div>}
              {refundPreview && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span>{bookingStrings.estimatedRefund}</span>
                    <span className="font-semibold text-foreground">
                      {formatMoney(refundPreview.amount, refundPreview.currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{bookingStrings.originalTotal}</span>
                    <span>{formatMoney(refundPreview.total, refundPreview.currency)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {bookingStrings.refundRate(refundPreview.percent)}
                  </div>
                  {refundPreview.amount === 0 && refundPreview.policyPercent === 0 && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                      {bookingStrings.refundOutside}
                    </div>
                  )}
                  {refundPreview.amount === 0 && refundPreview.policyPercent > 0 && (
                    <div className="rounded-lg border border-border bg-ghost/60 px-3 py-2 text-xs text-muted-foreground">
                      {bookingStrings.refundNoPayment}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                className="rounded-lg border border-border bg-ghost px-3 py-2 text-xs font-semibold text-ghost-foreground hover:opacity-80"
              >
                {bookingStrings.keepBooking}
              </button>
              <button
                type="button"
                onClick={requestCancel}
                disabled={previewLoading || cancelling || !!previewError || !refundPreview}
                className="rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50"
              >
                {cancelling ? bookingStrings.cancelling : bookingStrings.confirmCancellation}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
